/**
 * Pantalla de acceso. Única vía de entrada a Clica.
 *
 * Sin invitados anónimos: §5 de la especificación lo dice y el centro tiene Google Workspace, así
 * que todo el alumnado tiene cuenta. Por eso aquí no hay un atajo de «solo tengo un PIN»: llevaría
 * a una pantalla que rebota de vuelta a esta.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { ApiRequestError } from '../api/client.js';
import { supabase } from '../auth/supabase.js';
import { useSession } from '../auth/session.js';
import { SelectorIdioma } from '../components/selector-idioma.js';
import { Aviso } from '../components/sistema/aviso.js';
import { Boton } from '../components/sistema/boton.js';
import { Marca } from '../components/sistema/marca.js';

/** Dominio que Google preselecciona en el desplegador de cuentas, si está configurado. */
const HOSTED_DOMAIN = import.meta.env.VITE_GOOGLE_HOSTED_DOMAIN;

/**
 * Traduce la clave de error del servidor a lo que ve la persona.
 *
 * Los errores viajan como clave, nunca como texto ya traducido: el idioma lo resuelve el cliente
 * (principio V).
 */
function useRejectionMessage(error: ApiRequestError): { title: string; lead: string } {
  const { t } = useTranslation();
  switch (error.key) {
    case 'auth.error.domain_not_allowed':
      return {
        title: t('auth.error.domainNotAllowed'),
        lead: t('auth.error.domainNotAllowedLead'),
      };
    case 'auth.error.banned':
      return { title: t('auth.error.banned'), lead: t('auth.error.bannedLead') };
    default:
      return { title: t('auth.error.title'), lead: t('auth.error.generic') };
  }
}

function MensajeRechazo({ error }: { error: ApiRequestError }): React.JSX.Element {
  const { title, lead } = useRejectionMessage(error);
  return (
    <Aviso tono="atencion" interrumpe className="w-full max-w-md">
      <p className="font-display font-bold">{title}</p>
      <p className="text-body mt-1">{lead}</p>
    </Aviso>
  );
}

export function PantallaAcceso(): React.JSX.Element {
  const { t } = useTranslation();
  const state = useSession((store) => store.state);
  const [signingIn, setSigningIn] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // Las quince pantallas se llamaban igual en la pestaña y en el historial, con el proyector y el
  // portátil abiertos a la vez.
  useEffect(() => {
    document.title = `${t('auth.signInTitle')} · ${t('app.name')}`;
  }, [t]);

  async function signIn(): Promise<void> {
    setSigningIn(true);
    setFailure(null);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        // OBLIGATORIO. `GOTRUE_SITE_URL` de esta instancia apunta a otro de los proyectos
        // alojados, así que sin esto el alumno acabaría en la aplicación de ajedrez después de
        // identificarse.
        //
        // Es el ORIGEN desnudo, sin ruta: el allow-list de GoTrue solo contiene
        // `https://clica.jcamara.es` y `http://localhost:5173`, y compara con coincidencia
        // exacta. Una ruta de callback dedicada no casaría, y añadirla exigiría reiniciar el
        // GoTrue que comparten otros cuatro proyectos en producción. Adónde iba la persona se
        // guarda aparte, en `sessionStorage`, y lo consume el guardián de rutas al volver.
        redirectTo: window.location.origin,
        ...(HOSTED_DOMAIN === undefined || HOSTED_DOMAIN === ''
          ? {}
          : // Comodidad, no seguridad: preselecciona las cuentas del centro. La comprobación de
            // verdad la hace el servidor de Clica.
            { queryParams: { hd: HOSTED_DOMAIN } }),
      },
    });

    if (error !== null) {
      setSigningIn(false);
      setFailure(error.message);
    }
  }

  return (
    <main
      id="contenido"
      className="mx-auto flex min-h-dvh max-w-lg flex-col items-center justify-center gap-8 p-6 text-center"
    >
      <div className="flex flex-col items-center gap-4">
        <Marca size={104} className="text-edge" />
        <div>
          {/* `text-wrap: balance` lo pide `docs/marca.md` y no aparecía ni una vez en el código. */}
          <h1 className="font-display text-h1 font-extrabold tracking-tight text-balance">
            {t('app.name')}
          </h1>
          <p className="text-lead text-text-dim mt-1">{t('app.tagline')}</p>
        </div>
      </div>

      {state.status === 'rejected' && <MensajeRechazo error={state.reason} />}

      <div className="flex w-full max-w-sm flex-col items-center gap-4">
        <h2 className="font-display text-h3 font-bold">{t('auth.signInTitle')}</h2>
        <p className="text-body text-text-dim">{t('auth.signInLead')}</p>

        <Boton
          variante="primaria"
          ancho
          cargando={signingIn}
          onClick={() => void signIn()}
          className="text-lead"
        >
          {signingIn ? t('auth.signingIn') : t('auth.signInWithGoogle')}
        </Boton>

        {failure !== null && (
          <Aviso tono="error" interrumpe className="w-full">
            <p className="font-display font-bold">{t('auth.error.generic')}</p>
            {/* El proveedor devuelve su mensaje en inglés: se enseña como detalle técnico, no
                como la explicación principal. */}
            <p className="text-micro text-text-dim mt-1">
              {t('auth.error.technicalDetail', { code: failure })}
            </p>
          </Aviso>
        )}
      </div>

      <div className="flex flex-col items-center gap-4">
        <SelectorIdioma />
        {/* El enlace lleva su propia área táctil: como texto suelto medía 17 px de alto, por
            debajo de los 44 que pide WCAG 2.5.5. */}
        <Link
          to="/privacidad"
          className="text-micro font-display rounded-chip text-text-dim flex min-h-11 items-center px-3 font-bold uppercase underline underline-offset-4"
        >
          {t('privacy.link')}
        </Link>
      </div>
    </main>
  );
}
